import React, { useState, useEffect, useLayoutEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Header from './Layouts/Header';
import Footer from './Layouts/Footer';
import { getItemDetails, addToCart } from '../utils/apiService';

const ProductDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('desc');
    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(true);
    const [qty, setQty] = useState(1);
    const [addingToCart, setAddingToCart] = useState(false);
    const [currency, setCurrency] = useState('$'); // fallback default

    useLayoutEffect(() => {
        const timer = setTimeout(() => {
            if (window.animationText) window.animationText();
            if (window.scrollingEffect) window.scrollingEffect();
            if (window.animateImgItem) window.animateImgItem();

            if (window.ScrollTrigger) {
                window.ScrollTrigger.refresh();
            }
        }, 100);

        return () => {
            clearTimeout(timer);
            if (window.revertAnimations) window.revertAnimations();
            if (window.revertHeadline) window.revertHeadline();
        };
    }, [product]); // Re-run animations when product loads

    useEffect(() => {
        const fetchProductDetails = async () => {
            if (!id) return;
            setLoading(true);
            try {
                const data = await getItemDetails(id);
                if (data && data.status && data.data) {
                    setProduct(data.data);
                }
                  if (data.data.currency) {
                        setCurrency(data.data.currency);
                    }
            } catch (error) {
                console.error('Error fetching product details:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchProductDetails();
    }, [id]);

    const getImageUrl = (imgVal) => {
        if (!imgVal) return '';
        const imgStr = imgVal.toString().trim();
        // If it's a full URL, data URI, or already an absolute path
        if (imgStr.startsWith('http') || imgStr.startsWith('data:') || imgStr.startsWith('/')) {
            return imgStr;
        }
        // Otherwise, assume it's a storage path from the backend
        return `${process.env.REACT_APP_STORAGE_BASE_URL || 'http://localhost:8000/storage'}/${imgStr}`;
    };

    const handleAddToCart = async (e) => {
        e.preventDefault();

        // Check if user is logged in
        const token = localStorage.getItem('token');
        if (!token) {
            toast.info('Please login to add items to cart');
            navigate('/login');
            return;
        }

        setAddingToCart(true);

        try {
            const response = await addToCart(
                {
                    itemId: product.id,
                    quantity: qty
                },
                token
            );

            if (response && response.status) {
                toast.success(response.message || 'Item added to cart successfully!');
                // Update cart count in header
                window.dispatchEvent(new CustomEvent('cartUpdated'));
            } else {
                toast.error(response.message || 'Failed to add item to cart');
            }
        } catch (error) {
            console.error('Error adding to cart:', error);
            toast.error('An error occurred while adding to cart');
        } finally {
            setAddingToCart(false);
        }
    };

    if (loading) {
        return (
            <div className="wrapper position-relative">
                <Header />
                <div className="tf-container text-center py-5" style={{ marginTop: '100px' }}>
                    <h3>Loading product details...</h3>
                </div>
                <Footer />
            </div>
        );
    }

    if (!product) {
        return (
            <div className="wrapper position-relative">
                <Header />
                <div className="tf-container text-center py-5" style={{ marginTop: '100px' }}>
                    <h3>Product not found</h3>
                    <Link to="/" className="tf-btn style-border mt-3">Back to Home</Link>
                </div>
                <Footer />
            </div>
        );
    }

    return (
        <div className="wrapper position-relative">
            <Header />

            <div className="page-title">
                <div className="tf-container">
                    <div className="page-title-content text-center">
                        <h1 className="title">
                            <span className="split-text effect-right">Details</span>
                        </h1>
                        <div className="breadkcum">
                            <Link to="/" className="link-breadkcum body-2 fw-7">
                                <span className="split-text effect-right">Home</span>
                            </Link>
                            <span className="dot"></span>
                            <span className="page-breadkcum body-2 fw-7">
                                <span className="split-text effect-right" style={{ color: 'var(--primary)' }}>{product.title}</span>
                            </span>
                        </div>
                    </div>
                </div>
            </div>

            <div className="main-content">
                <section className="section-team p-team tf-spacing-2">
                    <div className="tf-container">
                        <div className="rg-30">
                            <div className="product-details-box">
                                <div className="row">
                                    {/* IMAGE */}
                                    <div className="col-md-5">
                                        <img
                                            src={getImageUrl(product.image)}
                                            alt={product.title}
                                            className="ls-is-cached lazyloaded product-details-img"
                                        />
                                    </div>

                                    {/* INFO */}
                                    <div className="col-md-7">
                                        <div className="mb-15">
                                            <h3>{product.title}</h3>
                                        </div>

                                        <div className="price-details">{currency}{Number(product.price).toLocaleString()}</div>

                                        <div className="in-stock">
                                            <p className="fw-semibold">
                                                <i className="fa fa-check-circle mr-2"></i>
                                                {product.quantity > 0 ? 'In Stock' : 'Out of Stock'}
                                            </p>
                                        </div>

                                        <div className="prodcut-short-description">
                                            <p dangerouslySetInnerHTML={{ __html: product.description }}></p>
                                        </div>

                                        <div className="d-flex align-items-center gap-3 mb-4">
                                            <input
                                                type="number"
                                                className="form-control qty-input"
                                                value={qty}
                                                onChange={(e) => setQty(Math.max(1, parseInt(e.target.value) || 1))}
                                                min="1"
                                            />
                                            <button
                                                onClick={handleAddToCart}
                                                disabled={addingToCart}
                                                className="tf-btn style-border justify-content-center"
                                            >
                                                <i className="fa fa-shopping-cart mr-2"></i>
                                                <span>{addingToCart ? 'Adding...' : 'Add to Cart'}</span>
                                            </button>
                                        </div>

                                        <div className="product-list-detials">
                                            <ul className="list-unstyled">
                                                <li><strong>Brand:</strong> {product.brand}</li>
                                                <li><strong>Category:</strong> {product.category}</li>
                                            </ul>
                                        </div>
                                    </div>
                                </div>

                                <div className="product-long-description">
                                    <ul className="nav nav-tabs mt-5" role="tablist">
                                        <li className="nav-item">
                                            <button
                                                className={`nav-link ${activeTab === 'desc' ? 'active' : ''}`}
                                                onClick={() => setActiveTab('desc')}
                                            >
                                                Description
                                            </button>
                                        </li>
                                    </ul>

                                    <div className="tab-content p-4">
                                        {activeTab === 'desc' && (
                                            <div className="tab-pane fade show active">
                                                <div className="desc-p" dangerouslySetInnerHTML={{ __html: product.description }}></div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </div>

            <Footer />
        </div>
    );
};

export default ProductDetails;
